export const APP_VERSION_PATH = '/app-version.json';
export const APP_UPDATE_POLL_MS = 60_000;
export const APP_UPDATE_RELOAD_COOLDOWN_MS = 15_000;

export type AppUpdateAction = 'none' | 'reload' | 'prompt';

export function appVersionUrl(nowMs: number): string {
  return `${APP_VERSION_PATH}?t=${nowMs}`;
}

export function parseAppVersionPayload(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const id = (raw as { buildId?: unknown }).buildId;
  if (typeof id !== 'string') return null;
  const trimmed = id.trim();
  return trimmed || null;
}

export function isNewerAppBuild(currentBuildId: string, remoteBuildId: string): boolean {
  const current = currentBuildId.trim();
  const remote = remoteBuildId.trim();
  if (!current || !remote) return false;
  return current !== remote;
}

/**
 * Hidden tabs and broken chunks reload; a visible tab only gets a prompt.
 * Background tabs must keep polling — otherwise a deploy is only noticed after
 * the user returns, at which point we would prompt instead of reload.
 */
export function resolveAppUpdateAction(params: {
  updateAvailable: boolean;
  hidden: boolean;
  brokenChunks: boolean;
}): AppUpdateAction {
  if (params.brokenChunks) return 'reload';
  if (!params.updateAvailable) return 'none';
  if (params.hidden) return 'reload';
  return 'prompt';
}

/** If a required reload is blocked by cooldown, still show the banner. */
export function shouldPromptWhenReloadBlocked(action: AppUpdateAction): boolean {
  return action === 'reload' || action === 'prompt';
}

/**
 * Skip polling only once an update is already known.
 * Hidden tabs must still poll so they can auto-reload in the background.
 */
export function shouldSkipAppUpdatePoll(params: {
  hidden: boolean;
  updateAlreadyKnown: boolean;
}): boolean {
  // Visibility only chooses reload vs prompt — background tabs still poll.
  return params.updateAlreadyKnown;
}

export function canReloadAfterCooldown(
  lastReloadAtMs: number | null,
  nowMs: number,
  cooldownMs: number = APP_UPDATE_RELOAD_COOLDOWN_MS,
): boolean {
  if (lastReloadAtMs == null) return true;
  return nowMs - lastReloadAtMs >= cooldownMs;
}

export type DeployReloadCooldownState = {
  /** False when the timestamp cannot be persisted across a reload. */
  persisted: boolean;
  lastReloadAtMs: number | null;
};

/** Automatic reloads need a persisted cooldown, otherwise missing chunks loop forever. */
export function canAutoReloadAfterDeploy(
  state: DeployReloadCooldownState,
  nowMs: number,
  cooldownMs: number = APP_UPDATE_RELOAD_COOLDOWN_MS,
): boolean {
  if (!state.persisted) return false;
  return canReloadAfterCooldown(state.lastReloadAtMs, nowMs, cooldownMs);
}
